// Messenger Phase 1 (Synced & Community Groups): builds the human-readable,
// localized text for a `system.<kind>` chat-group message from its raw
// payload. The server only ever stores/broadcasts structured payload data
// (`chatGroupService.createSystemMessage` — `content: JSON.stringify(payload)`)
// — formatting into a sentence is a client concern.
//
// Shared by BOTH delivery paths so a system message reads the same whether
// it arrives live or is loaded from history:
//   - live: MessengerPage's `handleSystemMessage` socket handler
//   - history: useMessages.js's `fetchMessagesForUser`/`loadMoreMessages`
// Before this was extracted, only the live path called it — a group's
// history (fetched over REST, e.g. the very first message when a group is
// created) rendered the raw JSON payload as literal message text instead of
// running it through this builder.
export const buildSystemMessageText = (type, payload = {}, t) => {
  const name = payload.name || `#${payload.infinityCode || ''}`;
  switch (type) {
    case 'system.member_joined':
      return t.systemMemberJoined.replace('{name}', name);
    case 'system.member_left':
      return t.systemMemberLeft.replace('{name}', name);
    case 'system.member_removed':
      return t.systemMemberRemoved.replace('{name}', name);
    case 'system.member_moved':
      return t.systemMemberMoved
        .replace('{name}', name)
        .replace('{from}', payload.fromDepartmentName || '')
        .replace('{to}', payload.toDepartmentName || '');
    case 'system.group_created':
      return t.systemGroupCreated;
    case 'system.group_name_changed':
      return t.systemGroupNameChanged;
    case 'system.group_photo_changed':
      return t.systemGroupPhotoChanged;
    case 'system.posting_mode_changed':
      return t.systemPostingModeChanged.replace(
        '{mode}',
        payload.to === 'ADMINS_ONLY' ? t.postingModeAdminsOnly : t.postingModeEveryone
      );
    case 'system.membership_mode_changed':
      if (payload.event === 'department_merged') {
        return t.systemDepartmentMerged
          .replace('{from}', payload.fromDepartment || '')
          .replace('{to}', payload.intoDepartment || '');
      }
      return t.systemMembershipModeChanged;
    default:
      return type.replace('system.', '').replace(/_/g, ' ');
  }
};

/** `msg.content` for a system-type row is a JSON string of the payload — safe-parse it. */
export const parseSystemMessagePayload = (content) => {
  if (!content || typeof content !== 'string') return {};
  try {
    return JSON.parse(content);
  } catch {
    return {};
  }
};

/** True for any chat-group row that's a system message, not a real user message. */
export const isSystemMessageType = (type) => typeof type === 'string' && type.startsWith('system.');
